import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { lookupsJson } from '@ourbranch/lookups';
import { v4 as uuidv4 } from 'uuid';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import { ActionButton } from 'core/components/action-button';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import addIcon from 'core/assets/svg/ic-add.svg';
import useStyles from '../liability-coverages-styles';

const customFeedingOptions = lookupsJson.farm.customFeedingOptions;

const getOperationLabel = (type) => customFeedingOptions.find((o) => o.id === type)?.value || type;

const validationSchema = Yup.object().shape({
  type: Yup.string().required('Operations is required'),
  description: Yup.string().required('Description is required'),
  associatedLocations: Yup.array().of(Yup.string().required('Associated location is required'))
});

const FIELD_NAME = 'farmLiabilityCoverage.customFeeding';

const CustomFeeding = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const items = getIn(values, FIELD_NAME) || [];

  const farmLocations = values.farmLocations || [];
  const locationOptions = farmLocations
    .filter((loc) => !loc.isDeleted)
    .map((loc) => ({ id: loc.id, value: loc.description || loc.id }));

  const resolveLocationName = (id) => locationOptions.find((o) => o.id === id)?.value || id;

  const coverageL = values.farmLiabilityCoverage?.coverageL;
  const annualAggregateLimit = values.farmLiabilityCoverage?.annualAggregateLimit;

  return (
    <div className={classes.sectionContainer}>
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Custom feeding</div>}
        initialOpen={items.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {items.map((item, index) => {
            const detailFields = [];
            detailFields.push({
              name: 'description',
              label: 'Description',
              value: item.description || 'N/A',
              size: 12
            });
            if (item.associatedLocations?.length) {
              detailFields.push({
                name: 'associatedLocations',
                label: 'Associated locations',
                value: item.associatedLocations.map(resolveLocationName).join(', '),
                size: 12
              });
            }

            return (
              <CollapsibleListItem
                key={`custom-feeding-${index}`}
                title={getOperationLabel(item.type)}
                rightSideValues={[]}
                detailFields={detailFields}
                onDelete={() => {
                  const newItems = items.filter((_, i) => i !== index);
                  setFieldValue(FIELD_NAME, newItems);
                  setFieldTouched(FIELD_NAME);
                  onChangeActive();
                }}
                disabled={disabled}
                keyPrefix={`custom-feeding-${index}`}
              />
            );
          })}

          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                type: '',
                description: '',
                associatedLocations: ['']
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(val, { resetForm }) => {
                const newItem = {
                  id: uuidv4(),
                  type: val.type,
                  description: val.description,
                  associatedLocations: val.associatedLocations.filter(Boolean)
                };
                setFieldValue(FIELD_NAME, [...items, newItem]);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
                resetForm();
              }}
            >
              {({ submitForm, values: formValues, setFieldValue: setInnerFieldValue }) => (
                <Grid container spacing={2}>
                  <Field
                    xs={12}
                    type="select"
                    id="type"
                    name="type"
                    mode="dark"
                    label="Operations"
                    options={customFeedingOptions}
                    disabled={disabled}
                  />
                  {formValues.type && (
                    <>
                      <Field
                        xs={6}
                        type="value"
                        id="customFeedingEachOccurrenceLimit"
                        name="customFeedingEachOccurrenceLimit"
                        mode="dark"
                        label="Custom feeding each occurrence limit"
                        formatter={() => (coverageL != null ? `$${Number(coverageL).toLocaleString()}` : 'N/A')}
                      />
                      <Field
                        xs={6}
                        type="value"
                        id="customFeedingAggregateLimit"
                        name="customFeedingAggregateLimit"
                        mode="dark"
                        label="Custom feeding aggregate limit"
                        formatter={() =>
                          annualAggregateLimit != null ? `$${Number(annualAggregateLimit).toLocaleString()}` : 'N/A'
                        }
                      />
                      <Field
                        xs={12}
                        type="textarea"
                        id="description"
                        name="description"
                        mode="dark"
                        label="Description (required)"
                        disabled={disabled}
                      />
                      {formValues.associatedLocations.map((_currentVal, index) => {
                        const selectedByOthers = formValues.associatedLocations.filter((val, i) => i !== index && val);
                        const availableOptions = locationOptions.filter((opt) => !selectedByOthers.includes(opt.id));

                        return (
                          <Grid item xs={12} key={`location-${index}`} container alignItems="center" wrap="nowrap">
                            <div className={classes.insuredFieldWrapper}>
                              <Field
                                xs={12}
                                type="select"
                                id={`associatedLocations.${index}`}
                                name={`associatedLocations.${index}`}
                                mode="dark"
                                label={`Associated location ${String(index + 1).padStart(2, '0')}`}
                                options={availableOptions}
                                disabled={disabled}
                              />
                            </div>
                            {index > 0 && (
                              <ActionButton
                                onClick={() => {
                                  const newLocations = formValues.associatedLocations.filter((_, i) => i !== index);
                                  setInnerFieldValue('associatedLocations', newLocations);
                                }}
                                className={classes.deleteInsuredButton}
                                icon={deleteIcon}
                                disabled={disabled}
                                size="small"
                              />
                            )}
                          </Grid>
                        );
                      })}
                      {formValues.associatedLocations.length < locationOptions.length && (
                        <Grid item xs={12}>
                          <Button
                            className={classes.addAnotherButton}
                            onClick={() =>
                              setInnerFieldValue('associatedLocations', [...formValues.associatedLocations, ''])
                            }
                            disabled={disabled}
                            startIcon={<img src={addIcon} alt="add" width={28} height={28} />}
                          >
                            Add another associated location
                          </Button>
                        </Grid>
                      )}
                      <Grid container alignItems="center" className={classes.addButtonRow}>
                        <Button
                          type="button"
                          mode="big"
                          variant="contained"
                          color="secondary"
                          className={classes.addButton}
                          disabled={disabled}
                          onClick={submitForm}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

CustomFeeding.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default CustomFeeding;
